import React, { useEffect, useState } from "react";
import { FaSpinner, FaChartLine, FaTrophy, FaPlus, FaMinus, FaCrown, FaLock } from "react-icons/fa";
import { consistencyToggle, consistencyStatus, consistencyAdd, consistencySubscriptionStatus, getLatestConsistency } from "../services/AIServices";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

const POLL_INTERVAL_MS = 30000; // 30s

const ConsistencyControl = () => {
    const [consistencyEnabled, setConsistencyEnabled] = useState(false);
    const [latestConsistency, setLatestConsistency] = useState(null);
    const [latestTrade, setLatestTrade] = useState(null);
    const [loading, setLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [error, setError] = useState("");
    const [inputValue, setInputValue] = useState(1);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [checkingSubscription, setCheckingSubscription] = useState(true);

    const checkSubscription = async () => {
        try {
            const res = await consistencySubscriptionStatus();
            if (res?.success) {
                setHasSubscription(res.hasSubscription || false);
            }
        } catch (e) {
            console.error("Failed to check subscription:", e);
        } finally {
            setCheckingSubscription(false);
        }
    };

    const fetchLatestConsistency = async () => {
        try {
            const res = await consistencyStatus();
            if (res?.success) {
                // Always set the running status
                setConsistencyEnabled(res.isRunning || false);
                
                // Set consistency data if available
                if (res?.consistency?.data) {
                    const consistencyData = res.consistency.data;
                    setLatestConsistency({
                        score: consistencyData.consistency_score || 0,
                        timestamp: consistencyData.created_at,
                        enabled: res.isRunning || false
                    });
                } else if (res.isRunning) {
                    // If running but no data yet, set default data
                    setLatestConsistency({
                        score: 0,
                        timestamp: new Date().toISOString(),
                        enabled: true
                    });
                }
            }
            setError("");
        } catch (e) {
            console.error("Failed to fetch consistency:", e);
            setError("Failed to fetch consistency data");
        }
    };

    const fetchLatestTrade = async () => {
        try {
            const res = await getLatestConsistency();
            if (res?.success && res?.result) {
                setLatestTrade({
                    tradeId: res.result.tradeId,
                    direction: res.result.direction,
                    count: res.result.count,
                    status: res.result.status
                });
            } else {
                // Clear trade data if no result available
                setLatestTrade(null);
            }
        } catch (e) {
            console.error("Failed to fetch latest trade:", e);
            setLatestTrade(null);
        }
    };

    useEffect(() => {
        // Check subscription first
        checkSubscription();
        
        // Initial fetch
        fetchLatestConsistency();
        fetchLatestTrade();
        
        // Poll for updates
        const id = setInterval(() => {
            fetchLatestConsistency();
            fetchLatestTrade();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggle = async () => {
        if (loading) return;
        
        setLoading(true);
        try {
            const res = await consistencyToggle();
            if (res?.success) {
                const newState = !consistencyEnabled;
                setConsistencyEnabled(newState);
                toast.success(res?.message || `Consistency ${newState ? 'enabled' : 'disabled'}`);
                await fetchLatestConsistency();
            } else {
                toast.error(res?.message || "Failed to toggle consistency");
            }
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Failed to toggle consistency";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCount = async () => {
        if (addLoading) return;
        
        // Validate input
        const numValue = parseInt(inputValue);
        if (isNaN(numValue) || numValue < 1 || numValue > 20) {
            toast.error("Value must be between 1 and 20");
            setInputValue(1);
            return;
        }
        
        setAddLoading(true);
        try {
            const res = await consistencyAdd(numValue);
            if (res?.success) {
                toast.success(res?.message || `Added ${numValue} to consistency count`);
                await fetchLatestConsistency();
                setInputValue(1); // Reset to minimum value
            } else {
                toast.error(res?.message || "Failed to add count");
            }
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Failed to add count";
            toast.error(msg);
        } finally {
            setAddLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        // Allow empty string or valid numbers
        if (value === '') {
            setInputValue('');
        } else {
            const numValue = parseInt(value);
            if (!isNaN(numValue)) {
                setInputValue(numValue);
            }
        }
    };

    const handleInputBlur = () => {
        // Clamp value to range on blur
        if (inputValue === '' || inputValue < 1) {
            setInputValue(1);
        } else if (inputValue > 20) {
            setInputValue(20);
        }
    };

    const handleIncrement = async () => {
        if (addLoading || !consistencyEnabled) return;
        
        const currentValue = parseInt(inputValue) || 1;
        const newValue = Math.min(20, currentValue + 1);
        setInputValue(newValue);
        
        // Auto-submit
        setAddLoading(true);
        try {
            const res = await consistencyAdd(newValue);
            if (res?.success) {
                toast.success(res?.message || `Added ${newValue} to consistency count`);
                await fetchLatestConsistency();
                setInputValue(1);
            } else {
                toast.error(res?.message || "Failed to add count");
            }
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Failed to add count";
            toast.error(msg);
        } finally {
            setAddLoading(false);
        }
    };

    const handleDecrement = async () => {
        if (addLoading || !consistencyEnabled) return;
        
        const currentValue = parseInt(inputValue) || 1;
        const newValue = Math.max(1, currentValue - 1);
        setInputValue(newValue);
        
        // Auto-submit
        setAddLoading(true);
        try {
            const res = await consistencyAdd(newValue);
            if (res?.success) {
                toast.success(res?.message || `Added ${newValue} to consistency count`);
                await fetchLatestConsistency();
                setInputValue(1);
            } else {
                toast.error(res?.message || "Failed to add count");
            }
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Failed to add count";
            toast.error(msg);
        } finally {
            setAddLoading(false);
        }
    };

    const score = latestConsistency?.score ?? 0;
    const scoreColor = 'text-blue-400';
    const scoreGradient = 'bg-gradient-to-r from-blue-500 to-indigo-400';

    // Show premium lock if no subscription
    if (checkingSubscription) {
        return (
            <div className="flex items-center justify-center p-12">
                <FaSpinner className="animate-spin text-3xl text-blue-500" />
            </div>
        );
    }

    if (!hasSubscription) {
        return (
            <div className="relative flex flex-col items-center w-full">
                <div className="relative w-full max-w-2xl rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-gray-900/60 via-gray-800/60 to-slate-900/60 backdrop-blur-sm shadow-2xl overflow-hidden">
                    {/* Premium Lock Overlay */}
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-8">
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full shadow-lg mb-4">
                                <FaCrown className="text-4xl text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Premium Feature</h3>
                            <p className="text-gray-300 mb-6 max-w-md">
                                Unlock Consistency AI to track your trading performance with AI-powered insights
                            </p>
                            <Link
                                to="/premium"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-semibold rounded-xl shadow-lg hover:from-yellow-600 hover:to-orange-700 transition-all hover:scale-105 active:scale-95"
                            >
                                <FaCrown />
                                <span>Upgrade to Premium</span>
                            </Link>
                        </div>
                    </div>
                    
                    {/* Blurred Content Preview */}
                    <div className="filter blur-sm pointer-events-none p-6 opacity-40">
                        <div className="flex items-center gap-2 mb-4">
                            <FaChartLine className="text-blue-400 text-lg" />
                            <div>
                                <h3 className="text-sm font-semibold text-white">Consistency Mode</h3>
                                <p className="text-[10px] text-gray-400">Premium Feature</p>
                            </div>
                        </div>
                        <div className="text-center py-8">
                            <div className="text-6xl font-bold text-blue-400 mb-2">0</div>
                            <div className="text-sm text-gray-400">Total Count</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col items-center w-full">
            {/* Main Card */}
            <div className="w-full max-w-2xl rounded-2xl border border-white/5 bg-gradient-to-br from-gray-900/60 via-gray-800/60 to-slate-900/60 backdrop-blur-sm shadow-2xl overflow-hidden">
                {/* Header with gradient overlay */}
                <div className="relative p-4 bg-gradient-to-r from-blue-600/15 to-cyan-600/15 border-b border-white/5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-blue-500/20 border border-blue-400/30">
                                <FaChartLine className="text-blue-400 text-lg" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-white">Consistency Mode</h3>
                                <p className="text-[10px] text-gray-400">Track trading performance</p>
                            </div>
                        </div>
                        
                        {/* Toggle Switch */}
                        <button
                            onClick={handleToggle}
                            disabled={loading}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                                consistencyEnabled 
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/30' 
                                    : 'bg-gray-600'
                            } ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:scale-105 active:scale-95'}`}
                            title={consistencyEnabled ? 'Disable consistency mode' : 'Enable consistency mode'}
                        >
                            <span
                                className={`inline-flex items-center justify-center h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                                    consistencyEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            >
                                {loading && (
                                    <FaSpinner className="h-3 w-3 text-blue-600 animate-spin" />
                                )}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {/* Status Badge */}
                    <div className="flex items-center justify-between">
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
                            consistencyEnabled 
                                ? 'border-blue-500/50 bg-blue-500/10' 
                                : 'border-gray-500/40 bg-gray-500/10'
                        }`}>
                            <span className={`inline-block h-2 w-2 rounded-full ${
                                consistencyEnabled ? 'bg-blue-400 animate-pulse' : 'bg-gray-500'
                            }`} />
                            <span className={`text-xs font-medium ${
                                consistencyEnabled ? 'text-blue-300' : 'text-gray-400'
                            }`}>
                                {consistencyEnabled ? 'Active' : 'Inactive'}
                            </span>
                        </div>

                        {latestConsistency && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-400/30">
                                <FaTrophy className="text-blue-400 text-xs" />
                                <span className="text-xs text-blue-300 font-medium">
                                    Count: {score}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Latest Trade Info */}
                    {latestTrade && (
                        <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-400/30 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-indigo-300">Latest Trade</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    latestTrade.status === 'continue' 
                                        ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                                        : 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/30'
                                }`}>
                                    {latestTrade.status?.toUpperCase()}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-xs">
                                <div className="bg-gray-800/50 rounded px-2 py-1">
                                    <div className="text-gray-400 text-[10px]">Trade ID</div>
                                    <div className="text-white font-semibold">#{latestTrade.tradeId}</div>
                                </div>
                                <div className="bg-gray-800/50 rounded px-2 py-1">
                                    <div className="text-gray-400 text-[10px]">Direction</div>
                                    <div className={`font-bold ${
                                        latestTrade.direction === 'UP' ? 'text-green-400' : 'text-red-400'
                                    }`}>
                                        {latestTrade.direction}
                                    </div>
                                </div>
                                <div className="bg-gray-800/50 rounded px-2 py-1">
                                    <div className="text-gray-400 text-[10px]">Count</div>
                                    <div className="text-blue-400 font-semibold">{latestTrade.count}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Score Display */}
                    {latestConsistency ? (
                        <div className="space-y-3">
                            {/* Score Header */}
                            <div className="flex items-center justify-center">
                                <div className="text-center">
                                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">
                                        Consistency Tracker
                                    </div>
                                    <div className={`text-6xl font-bold ${scoreColor} mb-2`}>
                                        {score}
                                    </div>
                                    <div className="text-sm text-gray-400 mb-4">
                                        Total Count
                                    </div>
                                    
                                    {/* Input and Add Button */}
                                    <div className="flex flex-col gap-2 w-full max-w-md mx-auto">
                                        <label className="text-xs text-gray-400 mb-1 block text-center">
                                            Add Value (1-20)
                                        </label>
                                        
                                        <div>
                                            {/* Input */}
                                            <input
                                                type="number"
                                                min="1"
                                                max="20"
                                                value={inputValue}
                                                onChange={handleInputChange}
                                                onBlur={handleInputBlur}
                                                disabled={!consistencyEnabled || addLoading}
                                                className={`w-full px-4 py-3 rounded-lg border text-center font-semibold text-xl md:text-2xl transition-all ${
                                                    consistencyEnabled
                                                        ? 'bg-gray-800 border-blue-500/30 text-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                                        : 'bg-gray-700 border-gray-600 text-gray-500 cursor-not-allowed'
                                                }`}
                                                placeholder="1-20"
                                            />
                                        </div>
                                        
                                        <button
                                            onClick={handleAddCount}
                                            disabled={addLoading || !consistencyEnabled}
                                            className={`w-full px-6 py-3 rounded-lg font-semibold text-base transition-all ${
                                                consistencyEnabled
                                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-blue-500/30'
                                                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                            } ${addLoading ? 'opacity-50 cursor-wait' : 'hover:scale-105 active:scale-95'}`}
                                            title={!consistencyEnabled ? 'Enable consistency mode first' : 'Add to count'}
                                        >
                                            {addLoading ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <FaSpinner className="animate-spin" />
                                                    <span>Adding...</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center gap-2">
                                                    <span>+</span>
                                                    <span>Add {inputValue} to Count</span>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Score Breakdown (if available) */}
                            {latestConsistency?.timestamp && (
                                <div className="pt-3 border-t border-white/5">
                                    <div className="text-[10px] text-gray-400 text-center">
                                        Last updated: {new Date(latestConsistency.timestamp).toLocaleString()}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 space-y-2">
                            <FaChartLine className="text-gray-600 text-3xl" />
                            <p className="text-sm text-gray-400 text-center">
                                No consistency data available
                            </p>
                            <p className="text-xs text-gray-500 text-center max-w-[200px]">
                                Enable mode to start tracking
                            </p>
                        </div>
                    )}
                </div>

                {/* Error Display */}
                {error && (
                    <div className="px-4 pb-4">
                        <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                            <span className="text-xs text-red-400">{error}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConsistencyControl;
